import { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Pagination, Table } from 'antd';
import { CustomIconFont } from 'icons';
import { difference } from 'lodash-es';

import { useRequest } from 'components/common';
import BaseOverflowTooltip from 'components/common/overflow-tooltip/BaseOverflowTooltip';
import { classnames } from 'lib/classnames';
import { DEFAULT_WORKSPACE_ICON } from 'lib/constants/icon';
import useI18n from 'lib/hooks/useI18n';
import { polyfillIcon } from 'lib/icons/utils';
import { Workspace } from 'lib/models';
import Parse, { escapeMatchesQueryArg } from 'lib/parse';

import styles from './index.module.less';

const cx = classnames(styles);

interface WorkspaceTableProps {
  keyword?: string;
  tableRef?: React.MutableRefObject<any>;
  selectedKeysRef?: React.MutableRefObject<string[]>;
}

const WorkspaceTable: React.FC<WorkspaceTableProps> = ({ tableRef, selectedKeysRef, keyword }) => {
  const { t } = useI18n();
  // 分页相关
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState([]);
  // 请求相关
  const parseQuery = useMemo(() => {
    const query = new Parse.Query(Workspace).addDescending('createdAt');
    if (keyword) {
      query.matches('name', escapeMatchesQueryArg(keyword), 'i');
    }
    const newParseQuery = Parse.Query.fromJSON(query.className, query.toJSON());
    newParseQuery.skip((pageIndex - 1) * pageSize);
    newParseQuery.limit(pageSize).withCount();
    return newParseQuery;
  }, [pageIndex, pageSize, keyword]);
  const { data: { results = [], count = 0 } = {}, isLoading } = useRequest({ parse: { parseQuery, initialData: [] } });
  useEffect(() => {
    if (!isLoading) {
      setTotal(count);
    }
  }, [results, count, setTotal, isLoading]);
  useEffect(() => {
    if (selectedKeysRef?.current) {
      setSelectedKeys(selectedKeysRef?.current);
    }
  }, [selectedKeysRef]);
  const paginationOnChange = (page, pageSize) => {
    setPageIndex(page);
    setPageSize(pageSize);
  };
  const rowSelection = {
    onSelect: (record, selected) => {
      setSelectedKeys(prev => {
        let current = [...prev];
        if (selected) {
          current.push(record.objectId);
        } else {
          current = current.filter(objectId => objectId !== record.objectId);
        }
        return current;
      });
    },
    onSelectAll: (selected, _, changeRows) => {
      const selectedRowsKeys = changeRows.map(row => row.objectId);
      setSelectedKeys(prev => {
        return selected ? [...prev, ...selectedRowsKeys] : difference(prev, selectedRowsKeys);
      });
    },
    selectedRowKeys: selectedKeys,
  };
  useImperativeHandle(tableRef, () => ({
    selectedRowKeys: selectedKeys,
  }));

  const columns = useMemo(
    () => [
      {
        title: t('pages.settings.system.reIndexing.workspaceTable.title'),
        key: 'name',
        dataIndex: 'name',
        render: (name, row) => {
          const [, icon] = polyfillIcon(row.icon, DEFAULT_WORKSPACE_ICON);
          return (
            <BaseOverflowTooltip className={cx('name')} title={name}>
              <span>
                {row.icon && <CustomIconFont type={icon} className={cx('img-icon')} />}
                <span className={cx('span')}>{name}</span>
              </span>
            </BaseOverflowTooltip>
          );
        },
      },
    ],
    [t],
  );

  return (
    <div>
      <Table
        columns={columns}
        dataSource={results}
        pagination={false}
        scroll={{ y: 420 }}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        rowKey={'objectId'}
        size="small"
      />
      <Pagination
        className="common-base-table-footer"
        current={pageIndex}
        total={total}
        pageSize={pageSize}
        onChange={paginationOnChange}
        showTotal={total => `${t('pages.settings.system.reIndexing.workspaceTable.total')}：${total}`}
      />
    </div>
  );
};
export default WorkspaceTable;
